/**
 * Time Formatting Utilities
 *
 * Unified time formatting with timezone handling and validation.
 * Prevents NaN errors and handles invalid timestamps gracefully.
 */

import { formatDistanceToNow, format, isToday, isYesterday, isThisYear } from 'date-fns';
import { zhCN } from 'date-fns/locale';

/**
 * Safely parse a timestamp to Date
 * Returns null if the timestamp is invalid
 */
export function safeParseDate(timestamp: string | number | Date | null | undefined): Date | null {
  if (!timestamp) return null;

  const date = typeof timestamp === 'object' ? timestamp : new Date(timestamp);

  if (isNaN(date.getTime())) {
    return null;
  }

  return date;
}

/**
 * Format timestamp with timezone handling for session list
 * Format: "X小时前" style relative time
 */
export function formatRelativeTime(timestamp: string | number | Date): string {
  const date = safeParseDate(timestamp);
  if (!date) {
    return '未知时间';
  }

  try {
    return formatDistanceToNow(date, { addSuffix: true, locale: zhCN });
  } catch {
    return '未知时间';
  }
}

/**
 * Format timestamp for message display
 * - Today: HH:MM
 * - Yesterday: 昨天 HH:MM
 * - This year: MM/DD HH:MM
 * - Older: YYYY/MM/DD HH:MM
 */
export function formatMessageTime(timestamp: string | number | Date): string {
  const date = safeParseDate(timestamp);
  if (!date) {
    return '无效时间';
  }

  try {
    if (isToday(date)) {
      return format(date, 'HH:mm');
    }

    if (isYesterday(date)) {
      return `昨天 ${format(date, 'HH:mm')}`;
    }

    if (isThisYear(date)) {
      return format(date, 'MM/dd HH:mm', { locale: zhCN });
    }

    return format(date, 'yyyy/MM/dd HH:mm', { locale: zhCN });
  } catch {
    return '无效时间';
  }
}

/**
 * Format timestamp as locale time string
 * Fallback to "无效时间" if parsing fails
 */
export function formatLocaleTime(timestamp: string | number | Date): string {
  const date = safeParseDate(timestamp);
  if (!date) {
    return '无效时间';
  }

  return date.toLocaleTimeString();
}
